import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { EventType } from 'components/Modal/AgnVotesEditor/AgnVotesEditor';
import Breadcrumb, {
  IBreadCrumbItem,
} from 'components/__ASSESSOR__/BreadCrumb/BreadCrumb';
import PrivateContainer from 'components/__SHARED__/PrivateContainer';
import { parseISO } from 'date-fns';
import { EditVoteCardKeys } from 'helpers/enums/Assembleia';
import { DateFormats } from 'helpers/enums/DateTypes';
import { Queries } from 'library/queries';
import { AssessorRoutePath } from 'library/Routes';
import translation from 'locales/translation';
import moment from 'moment';
import {
  Col,
  Collapse,
  Row,
  Title,
  ToastTypes,
  TopLoader,
} from 'plataforma-fundacao-componentes';
import { Suspense, useContext, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import { useVoteInfos } from 'services/editVotes';
import styles from './editarVotos.module.scss';
import ListAssociate from './fragments/editarVotos.associate';
import Cards from './fragments/editarVotos.cards';
import ListGuest from './fragments/editarVotos.guest';
import Staves from './fragments/editarVotos.staves';

export default function EditarVotos() {
  const history = useHistory();
  const params = useParams();

  const { showToast } = useContext(LayoutContext);

  const { data, isLoading, isError, error } = useVoteInfos(
    (params as { id: string }).id
  );

  const queryClient = useQueryClient();

  const breadcrumb = useMemo(
    (): IBreadCrumbItem[] => [
      { nome: translation.MainRoute.title, url: AssessorRoutePath },
      {
        nome: translation.Assessor.Evento.Vote.title,
      },
    ],
    []
  );

  const startDateInText = useMemo(() => {
    const startDate = parseISO(data?.startDate!);

    return `${translation.AssembleiaDateTime.day(
      startDate.getDate(),
      translation.AssembleiaDateTime[
        `month-${startDate.getMonth()}` as keyof typeof translation.AssembleiaDateTime
      ],
      startDate.getFullYear()
    )} ${moment(startDate).format(DateFormats.Time)}`;
  }, [data]);

  const cardData = useMemo(
    () => [
      {
        titleKey: EditVoteCardKeys.ASSOCIADOS_CONVOCADOS,
        value: data?.totals.convoked!,
      },
      {
        titleKey: EditVoteCardKeys.ASSOCIADOS_PRESENTES,
        value: data?.totals.present!,
      },
      {
        titleKey: EditVoteCardKeys.ASSOCIADOS_APTOS,
        value: data?.totals.ablePresent!,
      },
      {
        titleKey: EditVoteCardKeys.CONVIDADOS_PRESENTES,
        value: data?.totals.guests!,
      },
      {
        titleKey: EditVoteCardKeys.TOTAL_DE_PARTICIPANTES,
        value: data?.totals.totalPresent!,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  useEffect(() => {
    if (isError) {
      showToast({
        pauseOnFocusLoss: true,
        theme: ToastTypes.Error,
        showStatusBar: true,
        timeout: 4000,
        label:
          translation.Toasts.Errors.Keys[
            `${
              (error as AxiosError<Error>).response?.data.message ||
              (error as AxiosError<Error>).response?.data ||
              (error as AxiosError<Error>).message
            }` as keyof typeof translation.Toasts.Errors.Keys
          ] ?? translation.Toasts.Errors.defaultAlternative,
      });
    }
  }, [error, isError, showToast]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: [Queries.GetGuest] });
      queryClient.removeQueries({ queryKey: [Queries.GetVoteEditInfos] });
      queryClient.removeQueries({ queryKey: [Queries.GetBranchAndNucleou] });
    };
  }, [queryClient]);

  return (
    <Fragment>
      <Suspense fallback={<TopLoader opened />}>
        <PrivateContainer
          header={
            <Fragment>
              <Col>
                <Title
                  showBackButton
                  onBackClick={() => history.push(AssessorRoutePath)}
                >
                  {translation.Assessor.Evento.Vote.title}
                </Title>
              </Col>
              <Col>
                <Breadcrumb itens={breadcrumb} />
              </Col>
              <Row className={styles['big-title']}>
                <Collapse opened={Boolean(data?.title)} animateOpacity>
                  <Col>
                    <h2>{data?.title ?? '-'}</h2>
                  </Col>
                  <Col>
                    <p>
                      {data?.type} - {data?.city} - {startDateInText}
                      <br />
                      {translation.Assessor.Evento.Vote.address(data?.address!)}
                    </p>
                  </Col>
                </Collapse>
              </Row>
            </Fragment>
          }
          className={styles.root}
        >
          <Row centralized>
            {cardData.map((card, index) => (
              <Col cols={[12, 6, 2, 2, 2]} key={index}>
                <Cards
                  key={index}
                  titleKey={card.titleKey}
                  value={card.value}
                  loading={isLoading}
                />
              </Col>
            ))}
          </Row>
          <Row className={styles['big-title']}>
            <Col>
              <h2>{translation.Assessor.Evento.Vote.staves}</h2>
            </Col>
            <Collapse
              opened={Boolean(data && data?.agendas.length)}
              animateOpacity
            >
              <Col>
                <Staves
                  agendas={data?.agendas!}
                  type={data?.type! as EventType}
                />
              </Col>
            </Collapse>
            <Collapse
              opened={!Boolean(data && data?.agendas.length)}
              animateOpacity
            >
              <Col centralized>
                <h4>{translation.Assessor.Evento.Vote.withoutAgendas}</h4>
              </Col>
            </Collapse>
          </Row>
          <Row className={styles['big-title']}>
            <Col>
              <h2>Lista de Associados</h2>
            </Col>
            <ListAssociate />
          </Row>
          <Row className={styles['big-title']}>
            <Col>
              <h2>Lista de Convidados</h2>
            </Col>
            <ListGuest />
          </Row>
        </PrivateContainer>
      </Suspense>
    </Fragment>
  );
}
